import { useNavigate } from "react-router-dom";
import { ExternalLink, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { GuidanceSpec } from "./kaiTypes";

/**
 * Parse and validate a `guidance` fenced-block JSON payload. Returns `null` on
 * any shape mismatch so the caller can fall back to a placeholder.
 */
export function parseGuidanceSpec(json: string): GuidanceSpec | null {
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== "object") return null;
    if (!Array.isArray(parsed.steps) || parsed.steps.length === 0) return null;
    for (const step of parsed.steps) {
      if (!step || typeof step.text !== "string") return null;
      if (step.link !== undefined && typeof step.link !== "string") return null;
    }
    if (parsed.tutorial_id !== undefined && typeof parsed.tutorial_id !== "string") {
      return null;
    }
    if (
      parsed.practical_offer !== undefined &&
      typeof parsed.practical_offer !== "string"
    ) {
      return null;
    }
    return parsed as GuidanceSpec;
  } catch {
    return null;
  }
}

function isInternal(link?: string): link is string {
  return typeof link === "string" && link.startsWith("/");
}

interface KaiGuidanceProps {
  spec: GuidanceSpec;
  /**
   * Send the `practical_offer` back to Kai as a follow-up message. When
   * omitted, the "Ask Kai to do it" action is hidden.
   */
  onSendFollowUp?: (text: string) => void;
  /** Run before an internal navigation (e.g. close the Kai rail). */
  beforeNavigate?: () => void;
}

/**
 * Renders a `guidance` block as an actionable mini-guide: numbered steps with
 * "Go there" deep links and an optional "Ask Kai to do it" chip that sends the
 * practical offer as a follow-up. The v2 app has no tutorial system, so
 * `tutorial_id` is ignored.
 */
export function KaiGuidance({ spec, onSendFollowUp, beforeNavigate }: KaiGuidanceProps) {
  const navigate = useNavigate();

  const go = (link: string) => {
    beforeNavigate?.();
    navigate(link);
  };

  return (
    <div className="my-2 flex w-full min-w-0 max-w-full flex-col gap-3 overflow-hidden rounded-xl border border-border/50 bg-background/50 p-4">
      {spec.title && (
        <p className="text-sm font-semibold text-foreground">{spec.title}</p>
      )}
      <ol className="flex min-w-0 flex-col gap-2">
        {spec.steps.map((step, i) => (
          <li key={i} className="flex min-w-0 items-start gap-2 text-sm">
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/12 text-[11px] font-semibold text-primary">
              {i + 1}
            </span>
            <span className="min-w-0 flex-1 text-foreground/90">{step.text}</span>
            {isInternal(step.link) && (
              <Button
                variant="link"
                size="sm"
                className="h-auto shrink-0 gap-1 px-0"
                onClick={() => go(step.link as string)}
              >
                <ExternalLink className="size-3.5" />
                Go there
              </Button>
            )}
          </li>
        ))}
      </ol>

      {spec.practical_offer && onSendFollowUp && (
        <div className="min-w-0 w-full">
          <Button
            variant="outline"
            size="sm"
            className="h-auto w-full max-w-full justify-start gap-1.5 whitespace-normal py-2 text-left font-normal"
            onClick={() => onSendFollowUp(spec.practical_offer as string)}
          >
            <Sparkles className="size-3.5 shrink-0" />
            <span className="min-w-0 flex-1">{spec.practical_offer}</span>
          </Button>
        </div>
      )}
    </div>
  );
}
